import { useTranslation } from "react-i18next";
import { Button, ScrollArea } from "@/shared/ui";
import emptyStateImg from "@/shared/assets/img/notifications/empty-state.png";
import { useNotifications } from "../hooks";
import type { Notification } from "../types";
import NotificationCategoryFilter from "./NotificationCategoryFilter";
import NotificationItem from "./NotificationItem";
import NotificationItemSkeleton from "./NotificationItemSkeleton";

interface Props {
  onClose: () => void;
}

const MIN_SKELETON_ROWS = 1;
const MAX_SKELETON_ROWS = 5;

export default function NotificationsPanel({ onClose }: Props) {
  const { t } = useTranslation("Notifications");
  const {
    unreadCount,
    notifications,
    isFetching,
    hasNextPage,
    isMarkingAllRead,
    activeCategory,
    handleCategoryChange,
    handleLoadMore,
    handleNotificationClick,
    handleMarkAllRead,
  } = useNotifications(true);

  async function onNotificationClick(notification: Notification) {
    await handleNotificationClick(notification);
    onClose();
  }

  const skeletonRowCount = Math.min(Math.max(unreadCount, MIN_SKELETON_ROWS), MAX_SKELETON_ROWS);

  return (
    <div className="flex flex-col w-sm surface-base-fill radius-3xl overflow-hidden elevation-level-2">
      <div className="shrink-0 px-5 pt-5 pb-3 flex flex-col gap-3">
        <h3 className="text-heading-lg content-base-primary">{t("title")}</h3>
        <NotificationCategoryFilter
          activeCategory={activeCategory}
          onCategoryChange={handleCategoryChange}
          className="-mx-5 px-5 py-1"
        />
      </div>

      {isFetching && notifications.length === 0 ? (
        <div className="px-2 pt-1 pb-2">
          {Array.from({ length: skeletonRowCount }).map(function renderSkeletonRow(_, index) {
            return <NotificationItemSkeleton key={index} />;
          })}
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col gap-6 justify-center items-center px-5 pt-16 pb-20">
          <img src={emptyStateImg} alt="" width={100} className="w-25 aspect-square" />
          <div className="flex flex-col gap-1 justify-center items-center">
            <h6 className="text-heading-lg content-base-primary text-center">{t("emptyTitle")}</h6>
            <p className="text-body-regular-md content-base-secondary max-w-50 text-center">{t("emptyHint")}</p>
          </div>
        </div>
      ) : (
        <ScrollArea className="max-h-96 [&>[data-radix-scroll-area-viewport]]:overscroll-contain">
          <div className="px-2">
            {notifications.map((notification) => (
              <NotificationItem key={notification.id} notification={notification} onClick={onNotificationClick} />
            ))}
            {hasNextPage && (
              <div className="py-2">
                <Button variant="secondary" size="sm" className="w-full" disabled={isFetching} onClick={handleLoadMore}>
                  {t("loadMore")}
                </Button>
              </div>
            )}
          </div>
        </ScrollArea>
      )}

      {unreadCount > 0 && (
        <div className="shrink-0 px-2 pt-3 pb-5">
          <Button
            variant="secondary"
            size="md"
            className="w-full"
            disabled={isMarkingAllRead}
            onClick={handleMarkAllRead}>
            {t("markAllRead")}
          </Button>
        </div>
      )}
    </div>
  );
}
